import { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';
import cx from 'classnames';
import svgRight from '@plone/volto/icons/right-key.svg';
import { Icon, UniversalLink } from '@plone/volto/components';
import { useIntl } from 'react-intl';
import geoSphereMessages from '../../../messages';

const QuickLinks = () => {
  const ref = useRef(null);
  const intl = useIntl();
  const [openMainMenu, setOpenMainMenu] = useState(null);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [height, setHeight] = useState('auto');
  const computerBreakpoint = 1200;
  const fetchedRef = useRef(new Set());

  const dispatch = useDispatch();
  const lang = useSelector((state) => state.intl.locale);
  const quicklinksPath = `/${lang}/quicklinks`;
  const subrequest = `quicklinks-${lang}`;

  const content = useSelector(
    (state) => state.content.subrequests?.[subrequest],
  );

  const allSubrequests = useSelector((state) => state.content.subrequests);

  // Fetch top-level quicklinks folder
  useEffect(() => {
    if (!fetchedRef.current.has(subrequest)) {
      fetchedRef.current.add(subrequest);
      dispatch(getContent(quicklinksPath, null, subrequest, null, true));
    }
  }, [dispatch, quicklinksPath, subrequest]);

  // Extract top-level items
  const topLevelItems = useMemo(
    () => content?.data?.items || [],
    [content?.data?.items],
  );
  const topLevelLinks = useMemo(
    () => topLevelItems.filter((item) => item['@type'] === 'Link'),
    [topLevelItems],
  );
  const topLevelDocuments = useMemo(
    () => topLevelItems.filter((item) => item['@type'] === 'Document'),
    [topLevelItems],
  );

  // Fetch each top-level Document's children (category Documents)
  useEffect(() => {
    topLevelDocuments.forEach((doc) => {
      const docPath = flattenToAppURL(doc['@id']);
      const docSubrequest = `quicklinks-doc-${docPath}`;
      if (!fetchedRef.current.has(docSubrequest)) {
        fetchedRef.current.add(docSubrequest);
        dispatch(getContent(docPath, null, docSubrequest, null, true));
      }
    });
  }, [dispatch, topLevelDocuments]);

  // Collect all category Documents from all top-level Documents
  const allCategories = useMemo(() => {
    const result = [];
    topLevelDocuments.forEach((doc) => {
      const docPath = flattenToAppURL(doc['@id']);
      const docSubrequest = `quicklinks-doc-${docPath}`;
      const docContent = allSubrequests?.[docSubrequest];
      const cats = (docContent?.data?.items || []).filter(
        (item) => item['@type'] === 'Document',
      );
      result.push(...cats);
    });
    return result;
  }, [topLevelDocuments, allSubrequests]);

  // Fetch children for each category Document
  useEffect(() => {
    allCategories.forEach((cat) => {
      const catPath = flattenToAppURL(cat['@id']);
      const catSubrequest = `quicklinks-cat-${catPath}`;
      if (!fetchedRef.current.has(catSubrequest)) {
        fetchedRef.current.add(catSubrequest);
        dispatch(getContent(catPath, null, catSubrequest, null, true));
      }
    });
  }, [dispatch, allCategories]);

  // Build blocks (main quicklinks row — Links only)
  const blocks = useMemo(
    () =>
      topLevelLinks.map((item) => ({
        title: item.title,
        link: item.remoteUrl || flattenToAppURL(item['@id']),
      })),
    [topLevelLinks],
  );

  // Build dropdown data for each top-level Document
  const dropdowns = useMemo(
    () =>
      topLevelDocuments.map((doc) => {
        const docPath = flattenToAppURL(doc['@id']);
        const docSubrequest = `quicklinks-doc-${docPath}`;
        const docContent = allSubrequests?.[docSubrequest];
        const categories = (docContent?.data?.items || []).filter(
          (item) => item['@type'] === 'Document',
        );
        const subblocks = categories.map((cat) => {
          const catPath = flattenToAppURL(cat['@id']);
          const catSubrequest = `quicklinks-cat-${catPath}`;
          const catContent = allSubrequests?.[catSubrequest];
          const children = (catContent?.data?.items || []).map((child) => {
            const link = child.remoteUrl || flattenToAppURL(child['@id']);
            return {
              title: child.title,
              link,
            };
          });
          return {
            title: cat.title,
            link: flattenToAppURL(cat['@id']),
            children,
          };
        });
        return {
          title: doc.title,
          subblocks,
        };
      }),
    [topLevelDocuments, allSubrequests],
  );

  const isSubMenuOpen = openSubMenu !== null;

  useEffect(() => {
    const handleResizeOrScroll = () => {
      const width = window.innerWidth;
      if (width > computerBreakpoint && !openMainMenu) {
        setOpenMainMenu(true);
      }

      setIsMobile(width <= computerBreakpoint);

      if (isSubMenuOpen && openMainMenu && isMobile) {
        document.body.classList.add('quicklinks-submenu-open');
      } else {
        document.body.classList.remove('quicklinks-submenu-open');
      }

      ref.current.classList.remove('tablet');
      ref.current.style.top = '';

      if (ref.current && isMobile && openMainMenu && isSubMenuOpen) {
        const top = ref.current.getBoundingClientRect().top;
        setHeight(`calc(100vh - ${top}px)`);
        if (window.innerWidth > 650) {
          ref.current.classList.add('tablet');
          ref.current.style.setProperty('top', `${top}px`, 'important');
        }
      } else {
        setHeight('auto');
      }
    };

    handleResizeOrScroll();

    window.addEventListener('resize', handleResizeOrScroll);
    window.addEventListener('scroll', handleResizeOrScroll);

    return () => {
      window.removeEventListener('resize', handleResizeOrScroll);
      window.removeEventListener('scroll', handleResizeOrScroll);
    };
  }, [isSubMenuOpen, openMainMenu, isMobile]);

  return (
    <div
      ref={ref}
      style={{ height }}
      className={cx('quicklinks', {
        'submenu-open': isSubMenuOpen,
        'mainmenu-open': openMainMenu,
        mobile: isMobile,
      })}
      role="banner"
    >
      {
        <>
          <button
            className={cx('quicklinks-item quicklink-portals', {
              open: openMainMenu,
            })}
            onClick={() => setOpenMainMenu(!openMainMenu)}
          >
            <strong>
              {intl.formatMessage(geoSphereMessages.quicklinkPortals)}
            </strong>
            <Icon name={svgRight} size="16px" />
          </button>
          <div className="quicklinks-content-wrapper">
            <div className={`quicklinks-content ${openMainMenu ? 'open' : ''}`}>
              {blocks.map((block, index) => {
                return (
                  <UniversalLink
                    href={block.link}
                    key={index}
                    className="quicklinks-item"
                  >
                    <span>{block.title}</span>
                  </UniversalLink>
                );
              })}
              {dropdowns.map((dropdown, index) => (
                <button
                  key={`dropdown-${index}`}
                  className={cx('quicklinks-item all-links', {
                    open: openSubMenu === index,
                  })}
                  onClick={() =>
                    setOpenSubMenu(openSubMenu === index ? null : index)
                  }
                >
                  <span>{dropdown.title}</span>
                  <Icon name={svgRight} size="16px" />
                </button>
              ))}
            </div>
          </div>

          {/* <div className="separator"></div> */}
          {dropdowns.map((dropdown, index) => (
            <div
              key={`subcontent-${index}`}
              className="quicklinks-subcontent-wrapper"
            >
              <div
                className={`quicklinks-subcontent ${openSubMenu === index && openMainMenu ? 'open' : ''}`}
              >
                {dropdown.subblocks.map((subblock, subIndex) => {
                  return (
                    <div key={subIndex} className="subcontent-item">
                      <span className="headline">{subblock.title}</span>
                      <div className="subcontent-items">
                        {subblock.children.map((child, childIndex) => {
                          return (
                            <UniversalLink
                              href={child.link}
                              key={childIndex}
                              className={cx('quicklinks-item')}
                            >
                              <span>{child.title}</span>
                              <Icon name={svgRight} size="16px" />
                            </UniversalLink>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      }
    </div>
  );
};

export default QuickLinks;
